import { useTraduccion, type Clave } from '@tallerya/i18n';
import {
  Ayuda,
  AvisoError,
  Boton,
  CampoInterruptor,
  CampoSeleccion,
  CampoTexto,
  useAvisos,
  useFormulario,
  Ventana,
} from '@tallerya/ui';
import { useId, useState, type KeyboardEvent } from 'react';
import { useCambiosTipos, version, type Campo, type OpcionEnEdicion, type Tipo, type TipoDeDato } from './datos';

// Alta y edición de un campo de un tipo de activo (#117).
//
// La clave del campo en el JSONB y el valor de cada opción no se ven: los arma
// el sistema. **El tipo de dato se elige al crear y no se cambia**, porque los
// activos ya guardaron valores de ese tipo.
//
// La pantalla no empuja a marcar campos como obligatorios (#92): el interruptor
// nace apagado y su ayuda lo dice.

export type EdicionCampo = { tipo: 'nuevo' } | { tipo: 'editar'; campoId: string } | null;

interface Props {
  edicion: EdicionCampo;
  /**
   * El tipo **vivo**, no una copia de cuando se abrió la ventana: toda escritura
   * de un campo manda la versión del tipo (#93), y tras un conflicto la ficha se
   * vuelve a pedir; el siguiente «Guardar» tiene que llevar la nueva.
   */
  ficha: Tipo;
  onCerrar: () => void;
}

interface Valores {
  label: string;
  valueType: string;
  isRequired: boolean;
  opciones: OpcionEnEdicion[];
}

/** Cómo se nombra cada tipo de dato en la pantalla. Lo usa también `PanelCampos`. */
export const NOMBRE_DEL_DATO: Readonly<Record<TipoDeDato, Clave>> = {
  text: 'admin.tipos.dato.text',
  number: 'admin.tipos.dato.number',
  bool: 'admin.tipos.dato.bool',
  date: 'admin.tipos.dato.date',
  list: 'admin.tipos.dato.list',
};

const TIPOS_DE_DATO = Object.keys(NOMBRE_DEL_DATO) as TipoDeDato[];

/** Tope de una etiqueta, como `AssetTypeField.LabelMaxLength`. */
const LARGO_MAXIMO = 250;

/**
 * Cómo se comparan dos opciones: igual que arma la API su clave (`TechnicalKey`),
 * sin tildes ni mayúsculas y con un `_` donde había espacios o signos. «Diesel» y
 * «Diésel» son la misma; «Pick up» y «Pickup», no, porque para la API tampoco.
 */
function claveDeOpcion(texto: string): string {
  const clave = texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  // Y como la API, una clave que empieza por número lleva `c_` delante: para ella
  // «2 ejes» y «c 2 ejes» son la misma opción.
  if (clave === '') return 'c';
  return /^[0-9]/.test(clave) ? `c_${clave}` : clave;
}

/** Lo que falla en las opciones de una lista, o nada. Lo mismo que rechaza la API, dicho antes. */
function problemaDeOpciones(opciones: OpcionEnEdicion[]): string | undefined {
  if (opciones.length === 0 || opciones.some((o) => !o.label.trim())) return 'validation.required';
  const claves = opciones.map((o) => claveDeOpcion(o.label));
  if (new Set(claves).size !== claves.length) return 'asset_type_field.opciones_repetidas';
  return undefined;
}

export function VentanaCampo({ edicion, ficha, onCerrar }: Props) {
  // No cambia con la versión: lo escrito se conserva tras un conflicto.
  const clave = edicion === null ? 'cerrada' : edicion.tipo === 'nuevo' ? 'nuevo' : edicion.campoId;
  return <Contenido key={clave} edicion={edicion} ficha={ficha} onCerrar={onCerrar} />;
}

function Contenido({ edicion, ficha, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosTipos();
  const campo: Campo | null =
    edicion?.tipo === 'editar' ? (ficha.fields.find((c) => String(c.id) === edicion.campoId) ?? null) : null;

  const form = useFormulario<Valores>({
    inicial: {
      label: campo?.label ?? '',
      valueType: campo?.valueType ?? 'text',
      isRequired: campo?.isRequired ?? false,
      opciones: (campo?.allowedValues ?? []).map((o) => ({ label: o.label, value: o.value })),
    },
    validar: (v) => {
      const enOpciones = v.valueType === 'list' ? problemaDeOpciones(v.opciones) : undefined;
      return {
        ...(!v.label.trim() && { label: 'validation.required' }),
        ...(enOpciones && { opciones: enOpciones }),
      };
    },
    guardar: (v) => {
      const tipo = String(ficha.id);
      const versionTipo = version(ficha.version);
      return campo
        ? cambios.editarCampo(
            tipo,
            String(campo.id),
            versionTipo,
            v.label,
            v.isRequired,
            campo.valueType === 'list' ? v.opciones : null,
          )
        : cambios.agregarCampo(
            tipo,
            versionTipo,
            v.label,
            v.valueType as TipoDeDato,
            v.isRequired,
            v.opciones.map((o) => o.label),
          );
    },
    camposPorCodigo: {
      'asset_type_field.lista_sin_opciones': 'opciones',
      'asset_type_field.opciones_repetidas': 'opciones',
    },
  });

  const esLista = form.valores.valueType === 'list';

  async function guardar() {
    const nombre = form.valores.label.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: campo ? t('admin.tipos.aviso.campoGuardado', { nombre }) : t('admin.tipos.aviso.campoAgregado', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={campo ? t('admin.tipos.ventanaCampo.editar') : t('admin.tipos.ventanaCampo.nuevo')}
      info={t('admin.tipos.ventanaCampo.texto', { tipo: ficha.name })}
      ancho={520}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {campo ? t('admin.tipos.guardar') : t('admin.tipos.ventanaCampo.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="label"
        etiqueta={t('admin.tipos.campo.etiqueta')}
        obligatorio
        placeholder={t('admin.tipos.campo.etiquetaEjemplo')}
        autoComplete="off"
        ancho="completo"
      />
      {campo ? (
        // En la edición el tipo de dato se muestra, no se elige.
        <p className="tipos__dato-fijo">
          {t('admin.tipos.campo.datoFijo', { dato: t(NOMBRE_DEL_DATO[campo.valueType]) })}
        </p>
      ) : (
        <CampoSeleccion
          form={form}
          nombre="valueType"
          etiqueta={t('admin.tipos.campo.dato')}
          info={t('admin.tipos.campo.datoAyuda')}
          opciones={TIPOS_DE_DATO.map((d) => ({ valor: d, etiqueta: t(NOMBRE_DEL_DATO[d]) }))}
          ancho="completo"
        />
      )}
      {esLista && (
        <EditorDeOpciones
          opciones={form.valores.opciones}
          onCambiar={(opciones) => form.cambiar('opciones', opciones)}
          error={form.errores.opciones}
        />
      )}
      <CampoInterruptor
        form={form}
        nombre="isRequired"
        etiqueta={t('admin.tipos.campo.obligatorio')}
        info={t('admin.tipos.campo.obligatorioAyuda')}
      />
    </Ventana>
  );
}

/**
 * Las opciones de una lista: cada una se puede renombrar o quitar, y una nueva
 * se escribe abajo y se confirma con Enter o con «Agregar».
 *
 * Quitar una que ya existía no limpia los activos que la tenían
 * (`AssetType.EditField`): se avisa en el momento, no al guardar.
 */
function EditorDeOpciones({
  opciones,
  onCambiar,
  error,
}: {
  opciones: OpcionEnEdicion[];
  onCambiar: (opciones: OpcionEnEdicion[]) => void;
  error: string | undefined;
}) {
  const { t } = useTraduccion();
  const id = useId();
  const [nueva, setNueva] = useState('');
  const [quitadas, setQuitadas] = useState<string[]>([]);
  const [repetida, setRepetida] = useState<string | null>(null);

  function agregar() {
    const texto = nueva.trim();
    if (!texto) return;
    // Se frena acá y no al guardar: quien escribe la lista ve en el momento que
    // ya la tenía.
    if (opciones.some((o) => claveDeOpcion(o.label) === claveDeOpcion(texto))) {
      setRepetida(texto);
      return;
    }
    setRepetida(null);
    onCambiar([...opciones, { label: texto }]);
    setNueva('');
  }

  function alPresionar(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    agregar();
  }

  function quitar(indice: number) {
    const opcion = opciones[indice];
    if (opcion?.value) setQuitadas((q) => [...q, opcion.label]);
    onCambiar(opciones.filter((_, i) => i !== indice));
  }

  return (
    <fieldset
      className={`tipos__opciones${error || repetida ? ' tipos__opciones--error' : ''}`}
      aria-describedby={`${id}-ayuda ${id}-info`}
    >
      <legend className="ui-campo__etiqueta">
        {t('admin.tipos.opciones.titulo')}
        <span aria-hidden="true"> *</span>{' '}
        <Ayuda tema={t('admin.tipos.opciones.titulo')} texto={t('admin.tipos.opciones.ayuda')} />
      </legend>
      {/* Oculto, pero el lector lo lee al entrar al grupo (#121). */}
      <span id={`${id}-info`} hidden>
        {t('admin.tipos.opciones.ayuda')}
      </span>
      {opciones.length > 0 && (
        <ul className="tipos__opciones-lista">
          {opciones.map((o, i) => (
            <li key={o.value ?? `nueva-${i}`}>
              <input
                className="tipos__opcion"
                value={o.label}
                maxLength={LARGO_MAXIMO}
                aria-label={t('admin.tipos.opciones.nombre', { n: i + 1 })}
                onChange={(e) => onCambiar(opciones.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <Boton
                variante="texto"
                aria-label={t('admin.tipos.opciones.quitar', { nombre: o.label })}
                onClick={() => quitar(i)}
              >
                ×
              </Boton>
            </li>
          ))}
        </ul>
      )}
      <div className="tipos__opciones-nueva">
        <input
          className="tipos__opcion"
          value={nueva}
          maxLength={LARGO_MAXIMO}
          placeholder={t('admin.tipos.opciones.placeholder')}
          aria-label={t('admin.tipos.opciones.nueva')}
          onChange={(e) => {
            setNueva(e.target.value);
            setRepetida(null);
          }}
          onKeyDown={alPresionar}
        />
        <Boton variante="secundario" onClick={agregar} disabled={!nueva.trim()}>
          {t('admin.tipos.opciones.agregar')}
        </Boton>
      </div>
      <p id={`${id}-ayuda`} className="ui-campo__ayuda">
        {/* Debajo, solo el problema: la explicación va tras el ⓘ. */}
        {repetida ? t('admin.tipos.opciones.repetida', { nombre: repetida }) : error}
      </p>
      {quitadas.length > 0 && (
        <p className="tipos__aviso" role="status">
          {t('admin.tipos.opciones.quitadas', { nombres: quitadas.join(', ') })}
        </p>
      )}
    </fieldset>
  );
}
